/**
 * Server-supplied identifiers, rendered in the operator's language.
 *
 * Enum members are an open set: the Worker can start sending a value before
 * this bundle has learned its name. Every helper here therefore falls back to
 * `humanizeEnum`, which makes the raw id readable without inventing a meaning
 * for it — the same rule the app applies to an unrecognised hero or equipment
 * id (AGENTS.md, "Never hardcode unverified game mechanics").
 *
 * Rendering a guessed Korean label for a value we do not recognise would be the
 * one failure mode this file exists to prevent.
 */

import { translateEnum } from '../i18n';
import type { Dictionary } from '../i18n';
import { formatNumber, humanizeEnum } from './format';

export function detectedEventLabel(t: Dictionary, value: string): string {
  return translateEnum(t.enums.detectedEvent, value, humanizeEnum);
}

export function eventTypeLabel(t: Dictionary, value: string): string {
  return translateEnum(t.enums.eventType, value, humanizeEnum);
}

export function eventStatusLabel(t: Dictionary, value: string): string {
  return translateEnum(t.enums.eventStatus, value, humanizeEnum);
}

/**
 * Spec section 50. These labels describe how an operation was staffed; none of
 * them is an accusation, in either language.
 */
export function attendanceLabel(t: Dictionary, value: string): string {
  return translateEnum(t.enums.attendanceStatus, value, humanizeEnum);
}

export function growthFlagLabel(t: Dictionary, value: string): string {
  return translateEnum(t.enums.growthFlag, value, humanizeEnum);
}

export function rallyRoleLabel(t: Dictionary, value: string): string {
  return translateEnum(t.enums.rallyRole, value, humanizeEnum);
}

/**
 * A dashboard warning, rebuilt from its `code` and `params` (spec section 58).
 *
 * The server also sends a pre-formatted English `message`. That is the fallback
 * and nothing else: rendering it when a code IS recognised would leave the most
 * prominent banner on the dashboard in English for a Korean operator, which is
 * the bug this contract change exists to fix.
 *
 * Falls back to `message` in three cases, all of them deliberate:
 *   - `UNKNOWN`, the server's own escape hatch;
 *   - a code added to the API after this bundle shipped;
 *   - a recognised code whose params are missing or unusable.
 *
 * The last one matters. Interpolating an absent param would render
 * "Core alliance undefined is configured…" — a sentence that is worse than the
 * English one it replaced, because it looks like a data error in the alliance
 * rather than a gap in the payload.
 */
export function dashboardWarningText(
  t: Dictionary,
  warning: {
    code: string;
    params: Record<string, string | number | undefined>;
    message: string;
  },
): string {
  const text = t.dashboardWarnings;
  const { params } = warning;

  const stringParam = (key: string): string | null => {
    const value = params[key];
    return value === undefined || value === '' ? null : String(value);
  };

  const numberParam = (key: string): number | null => {
    const value = params[key];
    if (value === undefined) return null;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  switch (warning.code) {
    case 'NO_CORE_ALLIANCE':
      return text.NO_CORE_ALLIANCE;

    case 'CORE_ALLIANCE_NEVER_SYNCED': {
      const allianceId = stringParam('allianceId');
      return allianceId === null
        ? warning.message
        : text.CORE_ALLIANCE_NEVER_SYNCED(allianceId);
    }

    case 'CORE_ROSTER_STALE': {
      // `age` is already a compact label ("54m") built by the server against
      // its own freshness clock. It is passed through untouched rather than
      // reformatted, because this app does not hold the timestamp behind it.
      const age = stringParam('age');
      return age === null ? warning.message : text.CORE_ROSTER_STALE(age);
    }

    case 'API_BUDGET': {
      const used = numberParam('used');
      const budget = numberParam('budget');
      if (used === null || budget === null) return warning.message;
      return text.API_BUDGET(formatNumber(used) ?? String(used), formatNumber(budget) ?? String(budget));
    }

    case 'SYNC_FAILURES': {
      const failures = numberParam('failures');
      return failures === null ? warning.message : text.SYNC_FAILURES(failures);
    }

    default:
      return warning.message;
  }
}
